import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Expand, ArrowRight, FileText, CheckCircle, Loader2, Download } from "lucide-react";
import { loadJsPDF } from "@/utils/pdfEngines";
import { generateTemplateA, generateTemplateC, generateTemplateH } from "@/utils/paystubTemplates";
import { fetchPublishedLayout, renderLayout } from "@/utils/layoutEngine";
import { loadPdfJs } from "@/utils/pdfEngines";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Set up pdf.js worker

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

// Sample data for generating previews - default preview data
const SAMPLE_DATA = {
  // Company Info (template uses 'company', 'companyAddress', etc.)
  company: "MintSlip",
  companyAddress: "123 Financial Way",
  companyCity: "Austin",
  companyState: "TX",
  companyZip: "78701",
  companyPhone: "(512) 555-0100",
  ein: "12-3456789",

  // Employee Info (template uses 'name', 'address', 'city', etc.)
  name: "John Doe",
  address: "456 Oak Street",
  city: "Austin",
  state: "TX",
  zip: "78702",
  employeeId: "1234",
  ssn: "1234", // Just last 4 digits - template adds XXX-XX- prefix
  bank: "1234", // Just last 4 digits of bank account

  // Pay Info
  payFrequency: "biweekly",
  rate: 25.00,
  hours: 80,
  overtime: 0,
  commission: 0,
  bonus: 0,
  workerType: "employee",
  payType: "hourly",
  annualSalary: 52000,

  // Dates
  hireDate: "2023-06-15",
  periodStart: "2024-12-01",
  periodEnd: "2024-12-14",
  payDate: "2024-12-20",

  // Tax Info
  federalFilingStatus: "single",
  stateAllowances: "1",
  includeLocalTax: false,
  maritalStatus: "single",
  federalExemptions: "1",
  stateExemptions: "1",

  // ADP-specific fields
  companyCode: "MINT01",
  locDept: "100/ADM",
  checkNumber: "1001",
  pageNumber: "1",

  // Bank Info (for ADP template)
  bankName: "Mint Bank",
  bankLast4: "1234",
  routingNumber: "021000021",
  accountType: "Checking",

  // Workday-specific fields
  positionTitle: "Staff Accountant",
  department: "Finance",
  costCenter: "1000",

  // Deductions & Contributions (empty for clean sample)
  deductions: [],
  contributions: [],
  absencePlans: [],
  employerBenefits: [],
};

// Template configurations
const TEMPLATES = [
  {
    id: "template-a",
    name: "Gusto Style",
    description: "Clean, modern paystub design inspired by Gusto payroll. Features a compact layout with clear earnings breakdown, tax withholdings, and YTD totals. Perfect for small to medium businesses.",
    features: ["Modern clean design", "Compact single-page layout", "Clear earnings breakdown", "Employee & employer info sections"],
    generator: generateTemplateA,
    color: "#F4A460", // Orange peachy
  },
  // {
  //   id: "template-b",
  //   name: "ADP Style",
  //   description: "Professional enterprise-style paystub matching ADP's Earnings Statement format. Includes detailed tax withholding sections, marital status, and comprehensive deduction breakdowns.",
  //   features: ["Enterprise professional format", "Detailed tax breakdown", "Marital status & exemptions", "Check stub with bank info"],
  //   generator: generateTemplateB,
  //   color: "#d0271d", // ADP red
  // },
  {
    id: "template-c",
    name: "Workday Style",
    description: "Corporate HR-focused paystub design inspired by Workday. Features absence plan tracking, employer benefits section, and side-by-side comparison tables for detailed reporting.",
    features: ["Corporate HR style", "Absence plan tracking", "Employer benefits section", "Side-by-side tables"],
    generator: generateTemplateC,
    color: "#0066cc", // Workday blue
  },
  {
    id: "template-h",
    name: "OnPay Style",
    description: "Clean, modern paystub design inspired by OnPay payroll. Features a compact layout with clear earnings breakdown, tax withholdings, and YTD totals. Perfect for small to medium businesses.",
    features: ["Colorful modern design", "Three-column layout", "Direct deposit emphasis", "Accruals & ACH sections"],
    generator: generateTemplateH,
    color: "#000", // Orange accent
  },
];

// Convert PDF to image
async function convertPdfToImage(pdfDataUrl, scale = 1.5) {
  const base64Data = pdfDataUrl.split(',')[1];
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const loadingTask = (await loadPdfJs()).getDocument({ data: bytes.buffer });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;

  return canvas.toDataURL('image/png', 0.9);
}

// Generate MintSlip logo as data URL
function generateMintSlipLogo() {
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 60;
  const ctx = canvas.getContext('2d');

  // Clear background (transparent)
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw "MintSlip" text
  ctx.font = 'bold 36px "Segoe UI", Arial, sans-serif';
  ctx.fillStyle = '#1a4731'; // MintSlip dark green
  ctx.textBaseline = 'middle';
  ctx.fillText('MintSlip', 10, 32);

  return canvas.toDataURL('image/png');
}

// Generate sample preview for a template
async function generateSamplePreview(template) {
  const doc = new (await loadJsPDF())({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Generate MintSlip logo for the preview
  const mintSlipLogo = generateMintSlipLogo();

  // Calculate sample values
  const rate = SAMPLE_DATA.rate;
  const hours = SAMPLE_DATA.hours;
  const overtime = SAMPLE_DATA.overtime;
  const regularPay = rate * hours;
  const overtimeRate = rate * 1.5;
  const overtimePay = overtimeRate * overtime;
  const grossPay = regularPay + overtimePay;

  // Tax calculations (Texas has no state income tax)
  const ssTax = grossPay * 0.062;
  const medTax = grossPay * 0.0145;
  const federalTax = grossPay * 0.12;
  const stateTax = 0; // Texas - no state tax
  const localTax = 0;
  const totalTax = ssTax + medTax + federalTax + stateTax + localTax;
  const netPay = grossPay - totalTax;

  // YTD calculations (assuming 6 pay periods)
  const ytdMultiplier = 6;

  // Parse dates
  const startDate = new Date(SAMPLE_DATA.periodStart);
  const endDate = new Date(SAMPLE_DATA.periodEnd);
  const payDate = new Date(SAMPLE_DATA.payDate);

  const templateData = {
    formData: {
      ...SAMPLE_DATA,
      // These are already correct in SAMPLE_DATA with proper field names
    },
    rate,
    hours,
    overtime,
    overtimeRate,
    regularPay,
    overtimePay,
    grossPay,
    ssTax,
    medTax,
    federalTax,
    stateTax,
    localTax,
    totalTax,
    netPay,
    // Dates at top level for template
    startDate,
    endDate,
    payDate,
    payFrequency: SAMPLE_DATA.payFrequency,
    stubNum: 1,
    totalStubs: 1,
    // YTD values
    ytdRegularPay: regularPay * ytdMultiplier,
    ytdOvertimePay: overtimePay * ytdMultiplier,
    ytdGrossPay: grossPay * ytdMultiplier,
    ytdSsTax: ssTax * ytdMultiplier,
    ytdMedTax: medTax * ytdMultiplier,
    ytdFederalTax: federalTax * ytdMultiplier,
    ytdStateTax: stateTax * ytdMultiplier,
    ytdLocalTax: 0,
    ytdTotalTax: totalTax * ytdMultiplier,
    ytdNetPay: netPay * ytdMultiplier,
    ytdHours: hours * ytdMultiplier,
    ytdOvertimeHours: overtime * ytdMultiplier,
    ytdCommission: 0,
    ytdBonus: 0,
    ytdPayPeriods: ytdMultiplier,
    commission: 0,
    bonus: 0,
    // Deductions/Contributions
    deductionsData: [],
    contributionsData: [],
    absencePlansData: [],
    employerBenefitsData: [],
    totalDeductions: 0,
    totalContributions: 0,
    preTaxDeductions: 0,
    postTaxDeductions: 0,
    preTaxContributions: 0,
    postTaxContributions: 0,
    totalPreTax: 0,
    totalPostTax: 0,
    ytdDeductions: 0,
    ytdContributions: 0,
    // Pay type info
    payType: SAMPLE_DATA.payType,
    annualSalary: SAMPLE_DATA.annualSalary,
    workerType: SAMPLE_DATA.workerType,
    isContractor: false,
    // Logo
    logoDataUrl: mintSlipLogo,
    // Tax rates
    stateRate: 0,
    localTaxRate: 0,
    sutaRate: 0,
  };

  // Generate using the appropriate template
  await template.generator(doc, templateData, pageWidth, pageHeight, margin);

  const pdfDataUrl = doc.output('datauristring');
  return await convertPdfToImage(pdfDataUrl, 2);
}

// Template Card Component
function TemplateCard({ template, previewImage, isLoading, onUseTemplate }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      {/* Preview Image */}
      <div className="relative aspect-[8.5/11] bg-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : previewImage ? (
          <>
            <img
              src={previewImage}
              alt={`${template.name} preview`}
              className="w-full h-full object-cover object-top"
            />
            {/* Gradient overlay at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <FileText className="w-16 h-16 text-slate-300" />
          </div>
        )}

        {/* Template name badge */}
        <div
          className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-white text-sm font-semibold shadow-lg"
          style={{ backgroundColor: template.color }}
        >
          {template.name}
        </div>

        {/* Expand button */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button
              className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
              disabled={isLoading || !previewImage}
            >
              <Expand className="w-4 h-4 text-slate-700" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: template.color }}
                />
                {template.name} Preview
              </DialogTitle>
            </DialogHeader>
            {previewImage && (
              <div className="mt-4">
                <img
                  src={previewImage}
                  alt={`${template.name} full preview`}
                  className="w-full rounded-lg border border-slate-200 shadow-lg"
                />
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setDialogOpen(false);
                  onUseTemplate(template.id);
                }}
                className="inline-flex items-center gap-2 bg-[#0b0b0b] hover:bg-slate-800 text-white font-semibold px-6 py-3 rounded-full transition-colors"
              >
                Use This Template
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Card Content */}
      <div className="p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-2">
          {template.name}
        </h3>
        <p className="text-sm text-slate-600 mb-4 line-clamp-2">
          {template.description}
        </p>

        {/* Features list */}
        <ul className="space-y-1.5 mb-5">
          {template.features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        {/* Action Button */}
        <button
          onClick={() => onUseTemplate(template.id)}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#0b0b0b] hover:bg-slate-800 text-white font-semibold px-6 py-3 rounded-full transition-colors"
        >
          Use This Template
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Card shape for an admin-published custom template — its generator renders
// the published layout through the layout engine with the same sample data.
function toCustomCard(t) {
  return {
    id: `custom:${t.id}`,
    name: t.name,
    description: t.description || "Custom paystub template designed by the MintSlip team.",
    features: ["Custom MintSlip design", "Accurate tax calculations", "Instant PDF download"],
    color: t.badgeColor || "#34d399",
    generator: async (doc, templateData) => {
      const layout = await fetchPublishedLayout(t.id);
      if (!layout) throw new Error("Layout unavailable");
      renderLayout(doc, layout, templateData);
    },
  };
}

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

const WHY_CARDS = [
  {
    icon: CheckCircle,
    title: "Accurate Calculations",
    description: "All federal, state, and local taxes are automatically calculated based on current tax rates.",
    tone: "gray",
  },
  {
    icon: FileText,
    title: "Professional Design",
    description: "Templates designed to match industry-standard payroll formats from leading providers.",
    tone: "mint",
  },
  {
    icon: Download,
    title: "Instant Download",
    description: "Generate and download your paystub PDF instantly after completing your information.",
    tone: "black",
  },
];

export default function PaystubSamples() {
  const navigate = useNavigate();
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState({});
  const [customCards, setCustomCards] = useState([]);
  const [heroRef, heroInView] = useInView();
  const [gridRef, gridInView] = useInView();
  const [whyRef, whyInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // Admin-published custom paystub templates join the sample grid
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/doc-templates?documentType=paystub`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setCustomCards((d.templates || []).map(toCustomCard)); })
      .catch(() => {});
  }, []);

  // Generate previews on mount
  useEffect(() => {
    const generateAllPreviews = async () => {
      for (const template of TEMPLATES) {
        setLoading(prev => ({ ...prev, [template.id]: true }));
        try {
          const preview = await generateSamplePreview(template);
          setPreviews(prev => ({ ...prev, [template.id]: preview }));
        } catch (error) {
          console.error(`Error generating preview for ${template.name}:`, error);
        }
        setLoading(prev => ({ ...prev, [template.id]: false }));
      }
    };

    generateAllPreviews();
  }, []);

  // Previews for custom templates, as they load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const card of customCards) {
        if (previews[card.id] || loading[card.id]) continue;
        setLoading(prev => ({ ...prev, [card.id]: true }));
        try {
          const preview = await generateSamplePreview(card);
          if (!cancelled) setPreviews(prev => ({ ...prev, [card.id]: preview }));
        } catch (error) {
          console.error(`Error generating preview for ${card.name}:`, error);
        }
        if (!cancelled) setLoading(prev => ({ ...prev, [card.id]: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [customCards]); // eslint-disable-line

  const handleUseTemplate = (templateId) => {
    // Navigate to paystub form with template pre-selected
    navigate(`/app/paystubs?template=${templateId}`);
  };

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>Paystub Sample Templates | MintSlip - Preview Professional Pay Stub Designs</title>
        <meta name="description" content="Browse our collection of professional paystub templates. Preview Gusto and Workday style pay stubs. Choose your template and create accurate paystubs instantly." />
        <meta name="keywords" content="paystub templates, pay stub samples, paystub examples, Gusto paystub, Workday paystub, payroll templates, check stub templates" />
        <meta property="og:title" content="Paystub Sample Templates | MintSlip" />
        <meta property="og:description" content="Preview professional paystub templates. Choose from Gusto, ADP, and Workday styles. Create accurate pay stubs with automatic tax calculations." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Paystub Sample Templates | MintSlip" />
        <meta name="twitter:description" content="Browse and preview professional paystub templates. Multiple styles available for instant download." />
        <link rel="canonical" href="https://mintslip.com/paystub-samples" />
      </Helmet>

      <Header />

      {/* Hero — centered like the home hero */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-10 md:pt-20 md:pb-12">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="flex items-center justify-center gap-2 text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-4">
            <FileText className="w-4 h-4" />
            Professional Paystub Templates
          </p>
          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Paystub sample <span className="font-black">templates</span>
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Choose from our professionally designed paystub templates. Each template is crafted to meet industry standards and provides a clean, professional appearance for your payroll documents.
          </p>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
        </div>
      </section>

      {/* Templates Grid */}
      <section ref={gridRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Our paystub <span className="font-black">templates</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Select any template below to see a full preview or start creating your paystub right away. All templates include accurate tax calculations and professional formatting.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...TEMPLATES, ...customCards].map((template, index) => (
            <div
              key={template.id}
              className={`transition-all duration-700 ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + index * 120}ms` }}
            >
              <TemplateCard
                template={template}
                previewImage={previews[template.id]}
                isLoading={loading[template.id]}
                onUseTemplate={handleUseTemplate}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section ref={whyRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${whyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Why choose our <span className="font-black">templates?</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${whyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Our paystub generator provides professional, accurate documents trusted by thousands of businesses.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {WHY_CARDS.map((card, i) => {
            const t = toneCard[card.tone];
            return (
              <div
                key={card.title}
                className={`rounded-[28px] ${t.bg} p-8 md:p-10 transition-all duration-700 ${whyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <card.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-2xl md:text-3xl font-medium mb-3 ${t.big}`}>{card.title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{card.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="pb-20 md:pb-28 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Ready to create your <span className="font-black">paystub?</span>
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Choose any template above and start generating professional paystubs in minutes. No complicated setup required.
          </p>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
